import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import BreadCrumb from "../components/breadCrumb";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { FormatString } from "../helpers/stringHelpers";
import { checkCrud } from "../helpers/userRolesHelper";
import SellersPopup from "../popups/SellersPopup";
import { TranslationProvider } from "../providers/TranslationContext";
import { addNotification } from "../redux/actions";
import { apiDeleteSeller, apiGetAllSellers } from "../services/SellersService";

interface Props {
  contextLevel: string;
}

const entityName = 'seller';

const ProdejciPage = (props: Props) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Sellers' });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [] });
  const [sellerPopupData, setSellerPopupData] = useState({
    visible: false,
    id: undefined,
  });

  const dispatch = useDispatch();

  const [canCreate, setCanCreate] = useState(false);
  const [canRead, setCanRead] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const userInfo = useSelector((state: any) => state.userInfo);

  const setRoles = () => {
    setCanCreate(checkCrud(entityName, 'create', userInfo));
    setCanRead(checkCrud(entityName, 'read', userInfo));
    setCanUpdate(checkCrud(entityName, 'update', userInfo));
    setCanDelete(checkCrud(entityName, 'delete', userInfo));
  }

  useEffect(() => {
    setRoles();
  }, [userInfo.UserRoles])

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    apiGetAllSellers().then((responseData) => {
      setData({
        rows: responseData.data,
      });

      setLoading(false);
    });
  };

  const handleDeleteRow = (row) => {
    apiDeleteSeller(row.id)
      .then((responseData) => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteSuccess'), row.name), type: 'success' }));
        loadData();

        setLoading(false);
      })
      .catch((err: any) => {
        dispatch(addNotification({ text: FormatString(t('Popup.DeleteFailure'), row.name), type: 'error' }));
      });
  }

  const translationModel = { prefix: 'Sellers' };

  return (
    <TranslationProvider value={translationModel}>
      {sellerPopupData.visible && (
        <SellersPopup
          id={sellerPopupData.id}
          refreshData={() => loadData()}
          handleCancel={() => {
            setSellerPopupData({ visible: false, id: undefined });
          }}
        />
      )}
      <TopBarWithBreadCrumb
        contextLevel={props.contextLevel}
        pageLevel={t('Label')}
        rightAreaComponent={
          <AddButtonInTopBar
            setPopupData={setSellerPopupData}
            text={t('CreateText')}
            enabled={canCreate}
          />
        }
      />

      <PageContent>
        <DataGrid
          loading={loading}
          rows={data.rows}
          showDeleteButton={canDelete}
          showEditButton={canUpdate}
          handleEditAction={(id: any) => {
            setSellerPopupData({ visible: true, id: id });
          }}
          onDeleteRow={handleDeleteRow}
          cols={[
            { name: t('Fields.name'), dataField: "name", isLinkToItem: true, sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.identificationNumber'), dataField: "identificationNumber", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.vat'), dataField: "vat", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.email'), dataField: "email", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.currency'), dataField: "currency", sortable: true, filterable: true, filterType: "dropdown" },
            { name: t('Fields.city'), dataField: "city", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.mobile'), dataField: "mobile", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.phone'), dataField: "phone", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.zipCode'), dataField: "zipCode", sortable: true, filterable: true, filterType: "text" },
            { name: t('Fields.countryCode'), dataField: "countryCode", sortable: true, filterable: true, filterType: "text" },
          ]}
        />
      </PageContent>
    </TranslationProvider>
  );
};

export default ProdejciPage;
